import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { UseFieldArrayRemove, UseFieldArrayUpdate, useFormContext } from 'react-hook-form';
import { Dropdown } from '@app/components/form/Dropdown';
import { PackSelect } from '@app/components/form/selects/features/PackSelect';
import { NumberInput } from '@app/components/form/NumberInput';
import { ProductVariationSelect } from '@app/components/form/selects/features/ProductVariationSelect';
import { PromotionActionType, IPromotionCondition, IPromotionAction, PromotionConditionType } from '@app/common';
import { PromotionActionSelect } from '@app/components/form/selects/features/PromotionActionSelect';
import { PromotionItemDiscountStrategySelect } from './PromotionItemDiscountStrategySelect';
import { PromotionActionTargetSelect } from './PromotionActionTargetSelect';
import { getPromotionActionTargetAsIntelligibleLabel } from './promotion-form.helpers';

const usePromotionTargets = () => {
  const { watch } = useFormContext();

  const [conditions, actions] = watch(["conditions", "actions"]) as [IPromotionCondition[], IPromotionAction[]];

  const conditionEligibles = conditions.filter(condition => [PromotionConditionType.PACKS, PromotionConditionType.PRODUCT_VARIATIONS].includes(condition.type));
  const actionEligibles = actions.filter(action => [PromotionActionType.ADD_PACK, PromotionActionType.ADD_PRODUCT_VARIATION].includes(action.type));

  return [...actionEligibles, ...conditionEligibles]
    .map((item) => {
      const conditionIndex = conditions.findIndex(eligible => eligible.referenceId === item.referenceId)
      const actionIndex = actions.findIndex(eligible => eligible.referenceId === item.referenceId);

      const index = conditionIndex !== -1 ? conditionIndex : actionIndex;

      return { value: item.referenceId, label: getPromotionActionTargetAsIntelligibleLabel(item, index + 1) } as { value: string, label: string }
    });
}

const PromotionActionAddPackFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <PackSelect
        name={`actions.${index}.details.value`}
        label="Menu" />
    </Fragment>
  )
}

const PromotionActionAddProductVariationFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <ProductVariationSelect
        name={`actions.${index}.details.value`}
        label="Produit" />
    </Fragment>
  )
}

const PromotionActionOrderDiscountFixedFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <NumberInput
        min={0}
        name={`actions.${index}.details.value`}
        label="Montant (€)"
      />
    </Fragment>
  )
}

const PromotionActionOrderDiscountPercentageFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <NumberInput
        min={0}
        max={100}
        name={`actions.${index}.details.value`}
        label="Montant (%)"
      />
    </Fragment>
  )
}

const PromotionActionItemsDiscountFixedFields = ({ index }: { index: number }) => {
  const targets = usePromotionTargets();

  return (
    <Fragment>
      <NumberInput
        min={0}
        name={`actions.${index}.details.value`}
        label="Montant (€)"
      />

      <PromotionActionTargetSelect
        name={`actions.${index}.details.targets`}
        label="Cibles de la remise"
        data={targets}
        isMultiple
      />

      <PromotionItemDiscountStrategySelect
        name={`actions.${index}.details.strategy`}
        label="Stratégie de remise"
      />
    </Fragment>
  )
}

const PromotionActionItemsDiscountPercentageFields = ({ index }: { index: number }) => {
  const targets = usePromotionTargets();

  return (
    <Fragment>
      <NumberInput
        min={0}
        max={100}
        name={`actions.${index}.details.value`}
        label="Montant (%)"
      />

      <PromotionActionTargetSelect
        name={`actions.${index}.details.targets`}
        label="Cibles de remise"
        data={targets}
        isMultiple
      />

      <PromotionItemDiscountStrategySelect
        name={`actions.${index}.details.strategy`}
        label="Stratégie de remise"
      />
    </Fragment>
  )
}

const getDefaultDetailsValue = (type: PromotionActionType) => {
  switch (type) {
    case PromotionActionType.ADD_PACK:
      return {
        value: null,
        targets: [],
      }

    case PromotionActionType.ADD_PRODUCT_VARIATION:
      return {
        value: null,
        targets: [],
      }

    case PromotionActionType.ORDER_DISCOUNT_FIXED:
      return {
        value: null,
        targets: [],
      }

    case PromotionActionType.ORDER_DISCOUNT_PERCENTAGE:
      return {
        value: null,
        targets: [],
      }

    case PromotionActionType.ITEMS_DISCOUNT_FIXED:
      return {
        value: null,
        targets: [],
        strategy: null
      }

    case PromotionActionType.ITEMS_DISCOUNT_PERCENTAGE:
      return {
        value: null,
        targets: [],
        strategy: null
      }

  }
}

interface PromotionActionFormProps {
  index: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<any>;
}

export const PromotionActionForm = ({ index, isOpen, setIsOpen, remove, update }: PromotionActionFormProps) => {
  const name = `Action: ${index + 1}`;

  const { watch, formState: { errors } } = useFormContext();

  const [type, referenceId] = watch([`actions.${index}.type`, `actions.${index}.referenceId`]);
  const previousTypeRef = useRef(type);

  const error = Boolean(errors.actions) ? "Veuillez vérifier les champs" : undefined;

  useEffect(() => {
    if (previousTypeRef.current !== type && previousTypeRef.current !== undefined) {
      update(index, {
        type: type,
        referenceId,
        details: getDefaultDetailsValue(type),
      });
    }
    previousTypeRef.current = type;
  }, [type, index, referenceId, update]);

  const Fields = useMemo(() => {
    switch (type) {
      case PromotionActionType.ADD_PACK:
        return <PromotionActionAddPackFields index={index} />

      case PromotionActionType.ADD_PRODUCT_VARIATION:
        return <PromotionActionAddProductVariationFields index={index} />

      case PromotionActionType.ORDER_DISCOUNT_FIXED:
        return <PromotionActionOrderDiscountFixedFields index={index} />

      case PromotionActionType.ORDER_DISCOUNT_PERCENTAGE:
        return <PromotionActionOrderDiscountPercentageFields index={index} />

      case PromotionActionType.ITEMS_DISCOUNT_FIXED:
        return <PromotionActionItemsDiscountFixedFields index={index} />

      case PromotionActionType.ITEMS_DISCOUNT_PERCENTAGE:
        return <PromotionActionItemsDiscountPercentageFields index={index} />

      default:
        return null;
    }
  }, [type, index])

  return (
    <Dropdown
      label={name}
      error={error}
      isOpen={isOpen}
      handleOpen={setIsOpen}
      onDelete={() => remove(index)}>

      <PromotionActionSelect name={`actions.${index}.type`} />

      {Fields}

    </Dropdown>
  )
}